import React, { useEffect, useMemo, useState } from "react";
import { Tag, Popover } from "antd";
import * as XLSX from "xlsx";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
  UploadRowBucket,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import { deploymentNormApis } from "../../../../api/MachineryEquipment";
import ProjectScopeSelect from "../../shared/ProjectScopeSelect";
import {
  DEPLOYMENT_NORM_TEMPLATE_HEADERS,
  DEPLOYMENT_NORM_TEMPLATE_COL_WIDTHS,
  DEPLOYMENT_NORM_TEMPLATE_SAMPLE_ROWS,
  DEPLOYMENT_NORM_TEMPLATE_INSTRUCTIONS,
} from "./deploymentNormTemplate";

// Thin wrapper over the shared ExcelUploadStageModal (upload -> validating ->
// preview -> committing -> done | failed). Only the deployment-norm pieces
// live here: template generation, preview columns, row buckets, copy.
//
// PER-NORM-CODE VERSIONING: all rows sharing a Norm Code form one candidate
// matrix. Committing creates a NEW VERSION — effective from the commit
// instant — for each code whose matrix differs from the one in force; an
// identical matrix is No Change; codes absent from the file are untouched.
// Synthetic red "Removed" preview rows (no Excel row) show in-force lines the
// file's group drops. Backend reportType: 'deployment-norms-upload'.
//
// FACTORS ARE FRACTIONS in the sheet (0.05 = 5%) — matching the source
// workbook, unlike the equipment-rates sheet's whole-number percentages.

const generateDeploymentNormTemplate = () => {
  const headers = [...DEPLOYMENT_NORM_TEMPLATE_HEADERS];
  const sampleRows = DEPLOYMENT_NORM_TEMPLATE_SAMPLE_ROWS.map((row) => [...row]);
  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
  dataSheet["!cols"] = [...DEPLOYMENT_NORM_TEMPLATE_COL_WIDTHS];

  const instructionSheet = XLSX.utils.aoa_to_sheet(
    DEPLOYMENT_NORM_TEMPLATE_INSTRUCTIONS
  );
  instructionSheet["!cols"] = [{ wch: 22 }, { wch: 110 }];

  const workbook = XLSX.utils.book_new();
  // data sheet MUST be first — the backend parser reads the first worksheet
  XLSX.utils.book_append_sheet(workbook, dataSheet, "Equipment Norms");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "Instructions");
  XLSX.writeFile(workbook, "equipment-norms-upload-template.xlsx");
};

const cell = (value: any): string =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const actionTag = (action: string) =>
  action === "First Version" ? (
    <Tag color="green">First Version</Tag>
  ) : action === "New Version" ? (
    <Tag color="blue">New Version</Tag>
  ) : action === "No Change" ? (
    <Tag>No Change</Tag>
  ) : action === "Removed" ? (
    <Tag color="red">Removed</Tag>
  ) : action ? (
    <Tag>{action}</Tag>
  ) : (
    "—"
  );

const previewColumns: any[] = [
  {
    // Synthetic Removed rows carry no Excel row — they exist only in the
    // in-force matrix the file is about to supersede.
    title: "Row",
    dataIndex: "excelRow",
    key: "excelRow",
    width: 70,
    fixed: "left",
    render: (v: any) => (v === null || v === undefined ? "—" : v),
  },
  {
    title: "Norm Code",
    dataIndex: "normCode",
    key: "normCode",
    width: 110,
    render: cell,
  },
  {
    title: "Asset Code",
    dataIndex: "assetCode",
    key: "assetCode",
    width: 110,
    render: cell,
  },
  {
    title: "Equipment Name",
    dataIndex: "equipmentName",
    key: "equipmentName",
    width: 220,
    render: (v: any, record: any) => cell(v ?? record.assetName),
  },
  {
    title: "NPF",
    dataIndex: "nonProductionHoursFactor",
    key: "npf",
    width: 80,
    align: "right",
    render: cell,
  },
  {
    title: "EUF",
    dataIndex: "effectiveUtilizationFactor",
    key: "euf",
    width: 80,
    align: "right",
    render: cell,
  },
  {
    title: "AF",
    dataIndex: "applicationFactor",
    key: "af",
    width: 80,
    align: "right",
    render: cell,
  },
  {
    title: "Unit",
    dataIndex: "unit",
    key: "unit",
    width: 100,
    render: (v: any, record: any) => cell(v ?? record.uomName),
  },
  {
    title: "Prod. Norm",
    dataIndex: "prodNorm",
    key: "prodNorm",
    width: 100,
    align: "right",
    render: cell,
  },
  {
    title: "Action",
    dataIndex: "__action",
    key: "__action",
    width: 120,
    render: actionTag,
  },
  {
    title: "Error",
    key: "error",
    width: 100,
    render: (_: any, record: any) =>
      record?.Error ? (
        <Popover
          title="Row errors"
          content={
            <ul style={{ margin: 0, paddingLeft: 16, maxWidth: 420 }}>
              {(Array.isArray(record.Error) ? record.Error : [record.Error]).map(
                (err: string, i: number) => (
                  <li key={i}>{err}</li>
                )
              )}
            </ul>
          }
        >
          <Tag color="red" style={{ cursor: "pointer" }}>
            Error
          </Tag>
        </Popover>
      ) : (
        <Tag color="green">Valid</Tag>
      ),
  },
];

const bucketRows = (rows: any[]): UploadRowBucket[] => {
  const errorRows = rows.filter((row) => row?.Error);
  // First Version + New Version + Removed all shape a WRITTEN version;
  // 'No Change' codes write nothing.
  const versionRows = rows.filter(
    (row) =>
      !row?.Error &&
      (row?.__action === "First Version" || row?.__action === "New Version")
  );
  const removedRows = rows.filter(
    (row) => !row?.Error && row?.__action === "Removed"
  );
  const unchangedRows = rows.filter(
    (row) => !row?.Error && row?.__action === "No Change"
  );
  return [
    { key: "all", label: `All (${rows.length})`, rows },
    {
      key: "versions",
      label: `New Versions (${versionRows.length})`,
      rows: versionRows,
    },
    ...(removedRows.length
      ? [
          {
            key: "removed",
            label: `Removed (${removedRows.length})`,
            rows: removedRows,
          },
        ]
      : []),
    ...(unchangedRows.length
      ? [
          {
            key: "unchanged",
            label: `No Change (${unchangedRows.length})`,
            rows: unchangedRows,
          },
        ]
      : []),
    { key: "errors", label: `Errors (${errorRows.length})`, rows: errorRows },
  ];
};

const countNormCodes = (rows: any[]): number =>
  new Set(
    rows
      .filter((row) => !row?.Error && row?.normCode)
      .map((row) => String(row.normCode).trim().toLowerCase())
  ).size;

const deploymentNormUploadConfig: ExcelUploadTypeConfig = {
  title: "Upload Equipment Norms",
  entityLabel: "Equipment Norms",
  templateSource: { kind: "client", generate: generateDeploymentNormTemplate },
  startUpload: (formData) => deploymentNormApis.uploadExcel(formData),
  // Overridden per-instance below to carry the selected scope projectId.
  buildBody: (comments) => ({ comment: comments ? comments : "N/A" }),
  previewColumns,
  previewScrollX: 1300,
  bucketRows,
  summaryTags: (report: UploadReportState) => (
    <Tag color="blue">{countNormCodes(report.rows)} norm code(s)</Tag>
  ),
  rowErrorLabel: (row) =>
    row.excelRow
      ? `Row ${row.excelRow}`
      : row.normCode
      ? `${row.normCode} (in-force line)`
      : "Row",
  copy: {
    uploadIntro: (
      <p>
        You're about to import Equipment Norms — the rows sharing a Norm Code
        form that norm's complete equipment matrix. Machines are matched
        against Assets&nbsp;&rsaquo;&nbsp;Asset Master (by Asset Code first,
        then name). Committing creates a <strong>new version effective from
        the commit time for each changed norm code</strong>; an identical
        matrix is No Change, and codes not in the file are left untouched. A
        red <strong>Removed</strong> row is an in-force line your file drops —
        the new version will not carry it.
      </p>
    ),
    validatingHeading: "Validating your file…",
    validatingSub:
      "Matching machines to the Asset Master and comparing each norm code's matrix against the version in force. This normally takes a few seconds.",
    committingHeading: "Saving norm versions…",
    committingSub:
      "Inserting a new effective-dated version for each changed norm code. This normally takes a few seconds.",
    commitCta: "Save Norm Versions",
    doneHeading: "Equipment norm versions saved",
    doneSub: (report: UploadReportState) => (
      <>
        {countNormCodes(report.rows) || ""} norm code(s) processed,{" "}
        {report.successCount} line(s) written, effective from now. The list
        shows the new matrices immediately.
      </>
    ),
    creationFailedNote:
      "Save failed — no versions were written (the batch is all-or-nothing).",
  },
  discardOnCancelFromPreview: false,
};

const DeploymentNormUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId = 0,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Scope the uploaded norms to this projectId (0 = org master). */
  projectId?: number;
}) => {
  // Defaulted from the list's current selection but changeable here, so the
  // upload is self-contained.
  const [scopeProjectId, setScopeProjectId] = useState<number>(projectId);

  useEffect(() => {
    if (open) setScopeProjectId(projectId);
  }, [open, projectId]);

  const config = useMemo<ExcelUploadTypeConfig>(
    () => ({
      ...deploymentNormUploadConfig,
      buildBody: (comments: string) => ({
        comment: comments ? comments : "N/A",
        projectId: scopeProjectId,
      }),
      introSection: (
        <div className="mt-3">
          <label className="form-label mb-1">Upload into</label>
          <ProjectScopeSelect
            value={scopeProjectId}
            onChange={setScopeProjectId}
          />
          <div className="text-soft" style={{ fontSize: 12, marginTop: 4 }}>
            Norm codes are unique per scope (organization master or one
            project). Factors in the sheet are FRACTIONS — 0.05 means 5%.
            Total Working HRS is computed, never uploaded.
          </div>
        </div>
      ),
    }),
    [scopeProjectId]
  );

  return (
    <ExcelUploadStageModal
      config={config}
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      projectId={scopeProjectId}
    />
  );
};

export default DeploymentNormUploadModal;
